import { useState } from 'react';
import { t } from '../i18n';
import './DeleteCellarControl.css';

export type DeleteCellarOutcome = 'emptied' | 'missing';

/**
 * "Supprimer le contenu de ma cave" / "Supprimer cette cave" (T074, FR-039, FR-036,
 * contracts/ui-and-emails.md): nothing happens until the person has read a warning and ticked an
 * explicit confirmation. One warning for any cellar (owner, 2026-10-15).
 */
export function DeleteCellarControl({
  onDelete,
}: {
  onDelete: () => Promise<DeleteCellarOutcome>;
}) {
  const [open, setOpen] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | undefined>();
  // One label for one's own cellar and for another's (owner, 2026-10-08).
  const title = t('deleteCellar.action');

  function reset() {
    setOpen(false);
    setAcknowledged(false);
  }

  async function confirm() {
    setBusy(true);
    try {
      const outcome = await onDelete();
      setMessage(t(outcome === 'emptied' ? 'deleteCellar.done' : 'deleteCellar.missing'));
      reset();
    } catch {
      setMessage(t('deleteCellar.failed'));
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <div className="delete-cellar">
        <button
          type="button"
          className="delete-cellar__open"
          onClick={() => {
            setMessage(undefined);
            setOpen(true);
          }}
        >
          {title}
        </button>
        {message && <p role="status">{message}</p>}
      </div>
    );
  }

  return (
    <div className="delete-cellar delete-cellar--confirming" role="group" aria-label={title}>
      <p className="delete-cellar__warning">{t('deleteCellar.warning')}</p>
      <label className="delete-cellar__acknowledge">
        <input
          type="checkbox"
          checked={acknowledged}
          onChange={(event) => setAcknowledged(event.target.checked)}
        />
        {t('deleteCellar.acknowledge')}
      </label>
      <div className="delete-cellar__actions">
        <button type="button" onClick={reset} disabled={busy}>
          {t('common.cancel')}
        </button>
        <button
          type="button"
          className="delete-cellar__confirm"
          disabled={!acknowledged || busy}
          onClick={() => void confirm()}
        >
          {t('deleteCellar.confirm')}
        </button>
      </div>
      {message && <p role="status">{message}</p>}
    </div>
  );
}
